import { Box, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import format from 'date-fns/format'
import {
  getGoogleMapsLink,
  LocationMovementResult,
  LocationMovementSeverity,
  locationSeverityBands,
  MovementDetectionResult,
  MovementSeverity,
  movementSeverityBands,
  MAX_ANGULAR_DELTA
} from 'src/helpers'
import { OuterLink } from 'src/components/ui'

export const severityPalette: Record<MovementSeverity, { background: string; text: string }> = {
  slight: { background: '#e3f2fd', text: '#1565c0' },
  minor: { background: '#fff9c4', text: '#f57f17' },
  moderate: { background: '#ffe0b2', text: '#e65100' },
  major: { background: '#ffab91', text: '#bf360c' },
  critical: { background: '#ef9a9a', text: '#b71c1c' }
}

export const locationSeverityPalette: Record<LocationMovementSeverity, { background: string; text: string }> = {
  minor: severityPalette.minor,
  moderate: severityPalette.moderate,
  major: severityPalette.critical
}

const locationSeverityOrder: Record<LocationMovementSeverity, number> = { minor: 0, moderate: 1, major: 2 }

const LocationSeverityCell = ({ row }: GridRenderCellParams<LocationMovementResult>) => {
  const band = locationSeverityBands.find(({ severity }) => severity === row.severity) || locationSeverityBands[0]
  const colors = locationSeverityPalette[row.severity]

  return (
    <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, backgroundColor: colors.background }}>
      <Typography variant="body2" sx={{ color: colors.text, fontWeight: 600 }}>
        {band.label}
      </Typography>
    </Box>
  )
}

const SeverityCell = ({ row }: GridRenderCellParams<MovementDetectionResult>) => {
  const band = movementSeverityBands.find(({ severity }) => severity === row.severity) || movementSeverityBands[0]
  const colors = severityPalette[row.severity]

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Box sx={{ display: 'inline-block', px: 1, py: 0.25, mb: 0.5, borderRadius: 1, backgroundColor: colors.background }}>
        <Typography variant="body2" sx={{ color: colors.text, fontWeight: 600 }}>
          {band.label} · {row.maxDelta.toFixed(1)}°
        </Typography>
      </Box>
      <Box sx={{ width: '100%', height: 4, borderRadius: 2, backgroundColor: 'grey.200' }}>
        <Box sx={{ width: `${(row.maxDelta / MAX_ANGULAR_DELTA) * 100}%`, height: '100%', borderRadius: 2, backgroundColor: colors.text }} />
      </Box>
    </Box>
  )
}

export namespace SensorMovementDetectionColumns {
  export const Time: GridColDef<MovementDetectionResult> = {
    field: 'time',
    headerName: 'Datetime',
    minWidth: 220,
    editable: false,
    valueFormatter: ({ value }) => format(new Date(value), 'PPpp')
  }

  export const DeltaX: GridColDef<MovementDetectionResult> = {
    field: 'deltaX',
    headerName: 'ΔX (°)',
    type: 'number',
    minWidth: 110,
    editable: false,
    valueFormatter: ({ value }) => Number(value).toFixed(2)
  }

  export const DeltaY: GridColDef<MovementDetectionResult> = {
    field: 'deltaY',
    headerName: 'ΔY (°)',
    type: 'number',
    minWidth: 110,
    editable: false,
    valueFormatter: ({ value }) => Number(value).toFixed(2)
  }

  export const DeltaTiltZ: GridColDef<MovementDetectionResult> = {
    field: 'deltaTiltZ',
    headerName: 'ΔTilt (°)',
    type: 'number',
    minWidth: 110,
    editable: false,
    valueFormatter: ({ value }) => Number(value).toFixed(2)
  }

  export const Severity: GridColDef<MovementDetectionResult> = {
    field: 'maxDelta',
    headerName: 'Severity',
    type: 'number',
    minWidth: 200,
    editable: false,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params) => <SeverityCell {...params} />
  }
}

export namespace SensorLocationMovementColumns {
  export const Time: GridColDef<LocationMovementResult> = {
    field: 'time',
    headerName: 'Datetime',
    minWidth: 220,
    editable: false,
    valueFormatter: ({ value }) => format(new Date(value), 'PPpp')
  }

  export const Distance: GridColDef<LocationMovementResult> = {
    field: 'distance',
    headerName: 'Diff (m)',
    type: 'number',
    minWidth: 120,
    editable: false,
    valueFormatter: ({ value }) => Number(value).toFixed(1)
  }

  export const Severity: GridColDef<LocationMovementResult> = {
    field: 'severity',
    headerName: 'Severity',
    minWidth: 150,
    editable: false,
    sortComparator: (a: LocationMovementResult['severity'], b: LocationMovementResult['severity']) => locationSeverityOrder[a] - locationSeverityOrder[b],
    renderCell: (params) => <LocationSeverityCell {...params} />
  }

  export const Links: GridColDef<LocationMovementResult> = {
    field: 'links',
    headerName: 'Coordinates',
    minWidth: 180,
    editable: false,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2">
        <OuterLink to={getGoogleMapsLink(row.prevLatitude, row.prevLongitude)}>Previous</OuterLink>
        {' · '}
        <OuterLink to={getGoogleMapsLink(row.latitude, row.longitude)}>Current</OuterLink>
      </Typography>
    )
  }
}
